import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  loadInquiryDraft,
  saveInquiryDraft,
  submitInquiryDraft,
  type InquiryDraft,
} from '../lib/inquiryDraft'

const EmailConfirmPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const stateEmail = location.state?.email
  const email = stateEmail || params.get('email') || ''
  const [draft, setDraft] = useState<InquiryDraft | null>(null)
  const [currentEmail, setCurrentEmail] = useState(email)
  const [editEmail, setEditEmail] = useState(email)
  const [isEditing, setIsEditing] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusTone, setStatusTone] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const stored = loadInquiryDraft()
    setDraft(stored)
    const storedEmail = stored?.inquiry?.email ? String(stored.inquiry.email) : ''
    const resolvedEmail = email || storedEmail
    setCurrentEmail(resolvedEmail)
    setEditEmail(resolvedEmail)
  }, [email])

  const isValidEmail = (value: string) =>
    /^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/.test(String(value || '').trim())

  const handleResend = async () => {
    const nextEmail = editEmail.trim()
    setStatusMessage('')
    setStatusTone('')

    if (!isValidEmail(nextEmail)) {
      setStatusMessage('E-Mail-Adresse ist fehlerhaft.')
      setStatusTone('error')
      return
    }

    const stored = draft || loadInquiryDraft()
    if (!stored) {
      setStatusMessage('Keine gespeicherten Daten gefunden. Bitte starte den Vorgang neu.')
      setStatusTone('error')
      return
    }

    const updatedDraft = {
      ...stored,
      savedAt: new Date().toISOString(),
      inquiry: {
        ...stored.inquiry,
        email: nextEmail,
      },
    }

    saveInquiryDraft(updatedDraft)
    setDraft(updatedDraft)
    setIsSubmitting(true)

    try {
      const res = await submitInquiryDraft(updatedDraft)
      if (!res.ok && !res.redirected) {
        throw new Error(`Request failed with status ${res.status}`)
      }
      setCurrentEmail(nextEmail)
      setIsEditing(false)
      setStatusMessage('Neue Bestaetigung wurde gesendet.')
      setStatusTone('success')

      const nextParams = new URLSearchParams(location.search)
      nextParams.set('email', nextEmail)
      navigate(`/email-confirm?${nextParams.toString()}`, { replace: true })
    } catch (err) {
      console.warn('Failed to resend confirmation', err)
      setStatusMessage('Konnte die Anfrage nicht senden.')
      setStatusTone('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col">
      <header className="bg-[#9800B3] text-white px-4 py-3 flex items-center space-x-3 shadow">
        <Link to="/" className="flex items-center">
          <img src="/images/Logo.svg" alt="autoverkaufen.de" className="h-10 w-auto" />
        </Link>
      </header>

      <main className="flex-1 px-6 sm:px-8 flex flex-col items-center">
        <div className="w-full max-w-2xl bg-white shadow-md rounded-3xl p-6 sm:p-8 mt-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#9800B3] text-center leading-snug">
            Bestaetigungsmail gesendet
          </h1>

          <p className="text-center text-sm sm:text-base text-gray-800 max-w-xl mt-5 mx-auto">
            Wir haben dir soeben eine E-Mail geschickt. Bitte oeffne dein Postfach und bestaetige deine Adresse, damit wir
            fortfahren koennen.
          </p>

          {currentEmail ? (
            <div className="mt-6 bg-gray-100 border border-gray-200 rounded-2xl p-4 text-center">
              <p className="text-xs uppercase tracking-wide text-gray-500">Gesendet an</p>
              <p className="text-base font-semibold text-gray-900 mt-1">{currentEmail}</p>
            </div>
          ) : null}

          {isEditing ? (
            <div className="mt-6 space-y-3">
              <input
                type="email"
                value={editEmail}
                onChange={(event) => setEditEmail(event.target.value)}
                placeholder="E-Mail"
                className="w-full h-12 px-5 rounded-full border border-gray-200 bg-white text-gray-900 outline-none focus:border-[#0099ff]"
              />
              {statusMessage ? (
                <p
                  className={`text-xs ${
                    statusTone === 'error' ? 'text-red-600' : 'text-green-600'
                  }`}
                >
                  {statusMessage}
                </p>
              ) : null}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
                <button
                  onClick={handleResend}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-6 h-12 rounded-full bg-[#0099ff] text-white font-semibold shadow hover:bg-blue-600 transition disabled:opacity-70"
                >
                  {isSubmitting ? 'Sende...' : 'Neue Bestaetigung senden'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false)
                    setEditEmail(currentEmail)
                    setStatusMessage('')
                    setStatusTone('')
                  }}
                  className="w-full sm:w-auto px-6 h-12 rounded-full border border-[#0099ff] text-[#0099ff] font-semibold hover:bg-[#0099ff]/10 transition"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
              <button
                onClick={() => navigate('/schatzspreis')}
                className="w-full sm:w-auto px-6 h-12 rounded-full bg-[#0099ff] text-white font-semibold shadow hover:bg-blue-600 transition"
              >
                E-Mail-Adresse bestaetigen
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditEmail(currentEmail)
                  setIsEditing(true)
                }}
                className="w-full sm:w-auto px-6 h-12 rounded-full border border-[#0099ff] text-[#0099ff] font-semibold hover:bg-[#0099ff]/10 transition"
              >
                E-Mail-Adresse bearbeiten
              </button>
            </div>
          )}

          <p className="text-center text-xs text-gray-500 mt-5">
            Keine Mail erhalten? Schau im Spam-Ordner nach oder fordere den Link in ein paar Minuten erneut an.
          </p>
        </div>
      </main>

      <footer className="bg-[#323845] text-gray-200 pt-6 pb-8 mt-10">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-[#a6ff00]">autoverkaufen.de</span> - Die beste Wahl
            </div>
            <div>Häufige Fragen</div>
            <div>Kontakt</div>
            <div>Datenschutz</div>
            <div>Impressum</div>
          </div>
          <div className="flex justify-center sm:justify-end">
            <div className="w-14 h-14 rounded-full bg-[#0099ff] text-white flex items-center justify-center text-sm font-semibold">
              Hotline
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default EmailConfirmPage
